import { InfoTooltip, type LineData, TextWithEllipsis, tokens } from 'ComponentLibrary'
import { Box, Stack, useTheme } from '@mui/material'
import { Check } from 'react-feather'

type InteractiveLegendProps = {
    allSeries: LineData[]
    activeSeries: LineData[]
    setActiveSeries: (series: LineData[]) => void
    fullWidth?: boolean
}

export const MarketDataChartLegend = ({ allSeries, activeSeries, setActiveSeries, fullWidth }: InteractiveLegendProps) => {
    const isSerieActive = (serie: LineData) => !!activeSeries.find((activeSerie) => activeSerie.id === serie.id)

    const toggleSerie = (toggledSerie: LineData) => {
        if (isSerieActive(toggledSerie)) {
            setActiveSeries(activeSeries.filter((serie) => serie.id !== toggledSerie.id))
        } else {
            setActiveSeries([...activeSeries, toggledSerie])
        }
    }

    return (
        <Stack
            direction={fullWidth ? 'column' : 'row'}
            flexWrap="wrap"
            width="100%"
            columnGap={tokens.SizingSpacing200}
            rowGap={tokens.SizingSpacingNone}
        >
            {allSeries
                .filter((serie) => serie.data.some((point) => !!point))
                .toReversed()
                .map((serie) => (
                    <Stack
                        key={serie.id}
                        direction="row"
                        width={fullWidth ? '100%' : `calc(50% - ${tokens.SizingSpacing200})`}
                        padding={tokens.SizingSpacing025}
                        gap={tokens.SizingSpacing100}
                        alignItems="center"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => toggleSerie(serie)}
                    >
                        <CircleWithCheck color={serie.color} showCheck={isSerieActive(serie)} />
                        <Stack
                            direction="row"
                            alignItems="center"
                            maxWidth="calc(100% - 16px)"
                            maxHeight="21px"
                            gap={tokens.SizingSpacing025}
                        >
                            <TextWithEllipsis
                                tooltipText={serie.id}
                                typographyProps={{ variant: 'bodyTextMedium', color: tokens.ColorTextBold }}
                            >
                                {serie.id}
                            </TextWithEllipsis>
                            {serie.description && (
                                <Box alignItems="center">
                                    <InfoTooltip text={serie.description} />
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                ))}
        </Stack>
    )
}

type CircleWithCheckProps = {
    color?: string
    showCheck: boolean
}

const CircleWithCheck = ({ showCheck, color = '#000' }: CircleWithCheckProps) => {
    const theme = useTheme()

    return (
        <Box
            display="flex"
            height="16px"
            minWidth="16px"
            border={`2px solid ${color}`}
            sx={{ backgroundColor: showCheck ? color : 'none' }}
            borderRadius="100%"
            alignItems="center"
            justifyContent="center"
        >
            {showCheck && <Check size={12} color={theme.palette.white} />}
        </Box>
    )
}
